import { Bar, BarChart, CartesianGrid, Legend, Line, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import Spinner from 'react-spinkit';
import styles from '../AdministratorDashboard/DistrictReport/DistrictChart.module.scss';
import type { AttendanceChartPoint } from './attendanceReportUtils';

interface Props {
  data: AttendanceChartPoint[];
  isLoading: boolean;
}

const AttendanceTrendChart = ({ data, isLoading }: Props): JSX.Element => {
  return (
    <div className={styles.schoolContainer}>
      <ResponsiveContainer width='100%' height={500}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        ) : (
          <BarChart width={900} height={300} data={data}>
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='label' interval={0} tick={<CustomXAxisTick />} height={45} />
            <YAxis yAxisId='count' allowDecimals={false} />
            <YAxis yAxisId='rate' orientation='right' domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              yAxisId='count'
              dataKey='presentCount'
              name='Present'
              stackId='attendance'
              fill='#012A73'
              barSize={30}
            />
            <Bar yAxisId='count' dataKey='absentCount' name='Absent' stackId='attendance' fill='#00Afd8' barSize={30} />
            <Line
              yAxisId='rate'
              type='monotone'
              dataKey='attendanceRate'
              name='Attendance Rate'
              stroke='#ff6319'
              strokeWidth={3}
              dot={{ r: 3 }}
            />
          </BarChart>
        )}
      </ResponsiveContainer>
      <h3 className={styles.chartLabel}>Session:</h3>
    </div>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

const CustomXAxisTick = ({ x, y, payload }: TickProps) => {
  if (!payload?.value) {
    return null;
  }

  return (
    <Text fontSize={'14px'} width={75} x={x} y={y} textAnchor='middle' verticalAnchor='start'>
      {payload.value}
    </Text>
  );
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: AttendanceChartPoint;
    color?: string;
    fill?: string;
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const rowPayload = payload[0].payload;

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '4px',
      }}
    >
      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{rowPayload.periodLabel}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ margin: '4px 0', color: entry.fill || entry.color }}>
          {entry.name}: {entry.value}
          {entry.name === 'Attendance Rate' ? '%' : ''}
        </p>
      ))}
      <p style={{ margin: '4px 0', color: '#43484d' }}>Marked Students: {rowPayload.total}</p>
    </div>
  );
};

export default AttendanceTrendChart;
